/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { ColDef } from '@ag-grid-community/core';
import { AppState } from '@app/core/types';
import {
  ActionName,
  ClusterAction,
  ClusterActionCompletedResponse,
  ClusterActionFailedResponse,
  ClusterActionResponse,
  ClusterActionRunningResponse,
  ResponseStatus,
} from '@shared/types/cluster-actions';
import { IgniteCache, TimestampInMs } from '@shared/types/common';
import { ClusterNode } from '@shared/types/node';
import {
  ClusterActionQueryResponseResult,
  ClusterActionQueryStatisticsResult,
  ClusterActionRunningQueryResult,
  IgniteQueryType,
  Query,
  QueryExportMessage,
  QueryId,
  QuerySessionStatus,
  SqlMetadata,
} from '@shared/types/queries';
import { CursorId3x, CursorInfo3x, Query3xExecutionResult, QueryRunId3x } from '@shared/types/queries-3x';

export interface State extends AppState {
  queries: QueriesState;
}

export interface QueriesState {
  sqlQueries: Array<Query> | null;
  responses: Map<QueryId, ClusterActionResponse<ActionName.SQL_QUERY_EXECUTE | ActionName.SCAN_QUERY_EXECUTE>>;
  runningQueries3x: QueryId[];
  queryRuns3x: Map<QueryId, QueryRunId3x>;
  cursors3x: Map<QueryRunId3x, CursorInfo3x[]>;
  cursorPages3x: Map<CursorId3x, Query3xExecutionResult[]>;
  actions: {
    [status in ResponseStatus]?: ClusterAction<ActionName.SQL_QUERY_EXECUTE | ActionName.SCAN_QUERY_EXECUTE>[];
  };
  sqlMetadata: Map<SQLMetadataCompositeKey, SqlMetadata[]>;
  showSQLSidePanel: { advancedSettings: boolean; parameters: boolean };
  exports: {
    [status in ResponseStatus]?: QueryExportMessage[];
  };
}

export type QueryFormattedData = { [headerName: string]: string | number | boolean }[];

export interface ConvertedQueryResponseResult extends ClusterActionQueryResponseResult {
  columnDefs: ColDef[];
  data: QueryFormattedData;
  isExplain: boolean;
  isScanQuery: boolean;
}

export type ConvertedQueryActionResponseOk = ClusterActionCompletedResponse<ActionName> & {
  result: ConvertedQueryResponseResult[];
};

export type ConvertedQueryActionResponse =
  | ClusterActionRunningResponse
  | ClusterActionFailedResponse
  | ConvertedQueryActionResponseOk;

export interface QueryRouteParams {
  query: string;
}

export interface FrontendRunningQuery extends ClusterActionRunningQueryResult {
  nodeConsistentIds: string[];
}

export enum ReasonForDisablingSQLExecution {
  CLUSTER_ACTIVE,
  CLUSTER_UNINITIALIZED,
  CLUSTER_OFFLINE,
  INVALID_NODE,
  EMPTY_PARAMETERS,
  QUERY_IS_RUNNING,
}

// DB tree

export enum FolderName {
  SCHEMA = 'Schemas',
  CACHE = 'Caches',
  NODE = 'Nodes',
}

export type Cluster2xFolders = [
  { name: FolderName.SCHEMA; metadata: SchemaTree[] },
  { name: FolderName.CACHE; metadata: CacheTree[] },
  { name: FolderName.NODE; metadata: NodeTree[] },
];
export type Cluster3xFolders = [{ name: FolderName.SCHEMA; metadata: SchemaTree[] }];

export interface TreeRoot {
  id: string;
  name: string;
  folders: Cluster2xFolders | Cluster3xFolders;
  loaded: boolean;
  isLoading?: boolean;
}

export interface FolderTree {
  name: FolderName;
  metadata: CacheTree[] | SchemaTree[] | NodeTree[];
}
export type FolderRoot = TreeRoot & { folders: FolderTree[] };
export type CacheTree = IgniteCache;
export type NodeTree = ClusterNode;
export interface SchemaTree {
  name: string;
  tables: TableTree[];
}
export interface TableTree {
  name: string;
  fields: FieldTree[];
}
export interface FieldTree {
  name: string;
  type: string;
}

export interface QueryStatisticsElement extends ClusterActionQueryStatisticsResult {
  id: string;
}

export type SQLMetadataCompositeKey = string & { _tag_: 'SQLMetadataCompositeKey' };

export type QueryStatisticsActionArgument = { since: number };
export type RunningQueriesActionArgument = { duration: number };
export type QueryCancelActionArgument = { queryId: string };

/* Running Query Monitoring types */

type QueriesFilterConfigurationBase = {
  query: string | null;
  type: IgniteQueryType | null;
};

export type RunningQueriesConfiguration = QueriesFilterConfigurationBase & {
  duration: string | number | null;
};

export type MonitoringQueriesConfiguration = QueriesFilterConfigurationBase & {
  duration: number | null;
  startFrom: Date | string | null;
  startTo: Date | string | null;
  status: QuerySessionStatus | null;
};

export type RunningQueriesMonitoringFilter = {
  query?: string;
  queryType?: IgniteQueryType;
  startedAt?: number | [number, number]; // Same as in tracing
  duration?: string | number;
  status?: QuerySessionStatus;
};

export type RunningQueryInfo = {
  queryText: string | null;
  startedAt: TimestampInMs | null;
} | null;
